// Ported from LC2's DeinquencyGlance.js (commons/components/HomeTabs/Today/Portfolio/Glance/
// DeinquencyGlance.js — note the filename typo is LC2's own; this file is named/spelled
// correctly, matching how LC2's own Glance.js imports it under the corrected name). Same
// GlanceDelinquencyColumns shape (commons/constants/tableColumns.js: property_name,
// non_stakers ["All other residents", percentage], stakers ["Stakers earning Recurring Cash
// Back", percentage], reduction [percentage, red/green], state) and same "no data" empty
// state (see ./GlanceEmptyState.js).
// PORTED VERBATIM (including an apparent LC2 copy/paste leftover): LC2's row-mapping
// recomputes `days_on_market`/`days_on_market_comps` fields — evidently copied from
// DaysOnMarketGlance.js — that GlanceDelinquencyColumns never actually renders; kept here
// unchanged for fidelity, it's inert either way.
// SIMPLIFIED: table chrome rebuilt on ../components/DataTableShell.js instead of MUI X
// DataGrid Pro — see ./ConcessionGlance.js's header comment for why.
import React, {useMemo} from 'react';
import PropTypes from 'prop-types';
import {useSelector} from 'react-redux';

import {getCommunities} from '../../../app/store/reducers/entities';
import DataTableShell from '../components/DataTableShell';
import GlanceCell from './GlanceCell';
import GlanceEmptyState from './GlanceEmptyState';
import {getRandomValue} from './utils';

const COLUMN_SPECS = [
    {field: 'property_name', headerName: 'Property name'},
    {field: 'non_stakers', headerName: 'All other residents', format: 'percentage'},
    {field: 'stakers', headerName: 'Stakers earning Recurring Cash Back', format: 'percentage'},
    {field: 'reduction', headerName: 'Reduction', format: 'percentage', color: 'red/green'},
    {field: 'us_state', headerName: 'State'}
];

const DelinquencyGlance = ({data}) => {
    const communities = useSelector(getCommunities);

    const columns = useMemo(
        () =>
            COLUMN_SPECS.map((spec) => ({
                field: spec.field,
                headerName: spec.headerName,
                renderCell: (row) => <GlanceCell value={row[spec.field]} format={spec.format} color={spec.color} colorValue={spec.colorValue} />
            })),
        []
    );

    const rows = useMemo(() => {
        return (data || []).map((d) => {
            // See header comment: these two fields are unused by GlanceDelinquencyColumns,
            // kept only because LC2's own mapping computes them.
            const propDom = `${Math.round(d.days_on_market)}`;
            const compsDom = `${Math.round(d.days_on_market_comps)}`;
            const community = communities.find((c) => c.sfid === d.property_sfid);
            let usState = community?.full_address__statecode__s;

            if (!usState && community?.address__c) {
                const parts = community.address__c.split(' ');
                usState = parts[parts.length - 2];
            }

            return {
                ...d,
                id: getRandomValue(),
                days_on_market: propDom,
                days_on_market_comps: compsDom,
                us_state: usState
            };
        });
    }, [data, communities]);

    if (rows.length === 0) {
        return <GlanceEmptyState msg="No delinquency data found" type="Glance Metric" />;
    }

    return (
        <DataTableShell
            title="Delinquency"
            description="Compare delinquency for Stakers vs Non Stakers."
            columns={columns}
            rows={rows}
            getRowId={(row) => row.id}
            searchPlaceholder="Search Delinquency"
        />
    );
};

DelinquencyGlance.propTypes = {
    data: PropTypes.array
};

export default DelinquencyGlance;
